import axios from "axios";
import { AuthService } from "./AuthService";
import { isVendorRole } from "../components/helper/utility";
import { fetchMasterList, unwrapMasterPage, unwrapMasterRows } from "./MasterService";
import { SAP_PROXY_ENDPOINTS } from "../constants/sapEndpoints";
import { postSapProxy } from "./sapProxy";
import {
  createUploadSizeLimitError,
  validateUploadFormData,
} from "../utils/fileUploadLimits";
import moment from "moment";

const listMasterCompat = async (type, options = {}) => {
  const result = await fetchMasterList(type, {
    page: 0,
    size: 1000,
    ...options,
  });
  return { data: result.data, page: result.page };
};

const USER_API =
  process.env.REACT_APP_API_ENDPOINT + process.env.REACT_APP_USER_SERVICE_PORT;

const getApiHeader = () => {
  const acceptRole = AuthService.getAcceptRole();
  const apiHeader = {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + AuthService.currentUser()?.token,
      ...(acceptRole ? { "Accept-Role": acceptRole } : {}),
    },
  };
  return apiHeader;
};

const saveDocumentsAndImages = (userId, data, label) => {
  const sizeCheck = validateUploadFormData(data);
  if (!sizeCheck.ok) {
    return Promise.reject(createUploadSizeLimitError(sizeCheck));
  }

  const url = `${USER_API}user/saveImages/${userId}?label=${label}`;
  const apiHeaders = getApiHeader().headers;

  return axios.post(url, data, {
    headers: {
      ...apiHeaders,
      "Content-Type": "multipart/form-data",
    },
  });
};



const extractMappedVendorIdsFromPayload = (payload) => {
  const records =
    (Array.isArray(payload) && payload) ||
    payload?.result ||
    payload?.data?.result ||
    payload?.data ||
    [];
  if (!Array.isArray(records)) return [];

  const ids = [];
  records.forEach((row) => {
    if (!row || String(row.status) === "0") return;
    const single = row.vendorId ?? row.vendor_id;
    if (single != null && String(single).trim() !== "") {
      ids.push(String(single).trim());
    }
    const many = row.vendorIds ?? row.vendor_ids;
    if (Array.isArray(many)) {
      many.forEach((v) => {
        if (v != null && String(v).trim() !== "") ids.push(String(v).trim());
      });
    } else if (typeof many === "string" && many.trim()) {
      many.split(",").forEach((v) => {
        const t = v.trim();
        if (t) ids.push(t);
      });
    }
  });
  return [...new Set(ids)];
};

const pushVendorSearchFilter = (filters, field, operator, value) => {
  const values = (Array.isArray(value) ? value : [value])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean);
  if (!values.length) return;
  filters.push({
    field,
    operator: values.length > 1 && operator === "EQUAL" ? "IN" : operator,
    values,
  });
};

const emptyVendorListResponse = (page = 0, size = 5) => ({
  data: {
    result: [],
    count: 0,
    page: { page, size, totalElements: 0, totalPages: 0 },
  },
});

/** POST /vendor/search — same axios { data: { result, count } } shape as listVendor. */
const listVendor = async (
  page,
  count = 5,
  filters = {},
  dontCheckApproved = false
) => {
  const user = AuthService.currentUser() || {};
  const {
    status,
    country,
    id,
    bankKycLogStatus,
    vendorAccountGroup,
    ...restFilters
  } = filters || {};

  const searchFilters = [];
  const pageNum = page ?? 0;
  const size = count ?? 5;

  if (user.role === "MDM_TEAM_23") {
    pushVendorSearchFilter(searchFilters, "status", "EQUAL", status || "1");
  } else if (isVendorRole()) {
    pushVendorSearchFilter(searchFilters, "status", "EQUAL", status || "1");
    let mappedIds = [];
    try {
      const userId = user?.id ?? user?.userId;
      if (userId != null && userId !== "") {
        const mapping = await getVendorUserMappingByUserId(userId);
        mappedIds = extractMappedVendorIdsFromPayload(mapping)
          .map((v) => String(v).trim())
          .filter((v) => /^\d+$/.test(v));
      }
    } catch (err) {
      console.error("Failed to load vendor-user mapping for listVendor:", err);
      mappedIds = [];
    }
    if (!mappedIds.length) {
      return emptyVendorListResponse(pageNum, size);
    }
    pushVendorSearchFilter(searchFilters, "id", "IN", mappedIds);
  } else {
    const statusValues = String(status || "1, 2")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    pushVendorSearchFilter(
      searchFilters,
      "status",
      statusValues.length > 1 ? "IN" : "EQUAL",
      statusValues
    );
  }

  if (
    !dontCheckApproved &&
    (user.role === "SUPERVISOR" || user.role === "GLP_MANAGER")
  ) {
    pushVendorSearchFilter(searchFilters, "approved", "IN", ["1", "2", "3"]);
  }

  if (country) {
    pushVendorSearchFilter(searchFilters, "country", "EQUAL", country);
  }
  if (id) {
    pushVendorSearchFilter(searchFilters, "id", "EQUAL", id);
  }
  if (bankKycLogStatus) {
    pushVendorSearchFilter(
      searchFilters,
      "bankKycLogStatus",
      "EQUAL",
      bankKycLogStatus
    );
  }
  if (vendorAccountGroup) {
    pushVendorSearchFilter(
      searchFilters,
      "entityInformation.vendorAccountGroup",
      "EQUAL",
      vendorAccountGroup
    );
  }

  Object.entries(restFilters)
    .filter(([, value]) => value !== undefined && value !== "")
    .forEach(([key, value]) => {
      pushVendorSearchFilter(searchFilters, key, "LIKE", value);
    });

  const response = await axios.post(
    `${USER_API}vendor/search`,
    {
      page: { page: pageNum, size },
      sorts: [{ field: "modifiedDate", direction: "DESC" }],
      filters: searchFilters,
    },
    getApiHeader()
  );
  const payload = response.data;
  const rows = unwrapMasterRows(payload);
  const resultCount =
    payload?.page?.totalElements ??
    payload?.count ??
    payload?.totalElements ??
    payload?.total ??
    rows.length;

  return {
    ...response,
    data: {
      ...payload,
      result: rows,
      count: resultCount,
    },
  };
};

const searchVendors = (keyword, config = {}) => {
  const trimmed = String(keyword ?? "").trim();
  if (!trimmed) {
    return Promise.resolve({ data: [] });
  }

  return axios.get(`${USER_API}vendor/search`, {
    params: { keyword: trimmed },
    ...getApiHeader(),
    signal: config.signal,
  });
};

// const listVendor = (page, count = 5, filters = {}) => {
//   const user = AuthService.currentUser();
//   let query = {};
//   let searchQuery = "";
//   if (filters.status) {
//     searchQuery = `t.status = ${filters.status}`;
//   } else {
//     searchQuery = `t.status=1`;
//   }

//   // Dynamically append filters to the searchQuery
//   const { status, country, ...restFilters } = filters;
//   const filterConditions = Object.entries(restFilters)
//     .filter(([key, value]) => value !== undefined && value !== "")
//     .map(([key, value]) => `t.${key} LIKE '%${value}%'`)
//     .join(" OR ");

//   if (filterConditions) {
//     searchQuery += ` AND (${filterConditions})`;
//   }

//   if (filters.country) {
//     searchQuery += ` AND t.country = '${filters.country}'`;
//   }

//   if (user.role === "VENDOR") {
//     searchQuery += ` AND t.email ='${user.email}'`;
//   } else if (user.role === "MDM_TEAM_23") {
//     // No additional filters needed for MDM TEAM
//   } else {
//     if (filters.status) {
//       searchQuery = ` t.status = ${filters.status}`;
//     } else {
//       searchQuery = `t.status IN (1, 2)`;
//     }
//     if (filters.country) {
//       searchQuery += ` AND t.country = '${filters.country}'`;
//     }
//     if (filterConditions) {
//       searchQuery += ` AND (${filterConditions})`;
//     }
//   }

//   searchQuery += " order by t.modifiedDate DESC";
//   query = {
//     searchQuery: searchQuery,
//     pageIndex: page,
//     maxResults: count,
//   };
//   return axios.post(`${USER_API}vendor/listVendor`, query, getApiHeader());
// };

const saveVendor = (data) => {
  return axios.post(`${USER_API}vendor/saveVendor`, data, getApiHeader());
  //   return axios.post('http://localhost:8082/api/vendor/saveVendor', data, getApiHeader());
};

const createVendor = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.VENDOR_CREATION, data);

const vendorChange = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.VENDOR_CHANGE, data);

const vendorBlockUnblock = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.VENDOR_BLOCK_UNBLOCK, data);

const getPlant = (options = {}) => listMasterCompat("plant", options);

const getPlantsByCompanyCode = (companyCode) => {
  if (!companyCode) return Promise.resolve({ data: [] });
  return axios.get(`${USER_API}plant/company/${encodeURIComponent(companyCode)}`, getApiHeader());
};

const getAllStoragePlants = (options = {}) =>
  listMasterCompat("storagePlant", options);

const getPlantPurchaseGroup = (options = {}) =>
  listMasterCompat("plantPurchaseGroup", options);

const savePlantPurchaseGroup = (data) => {
  return axios.post(`${USER_API}plant-purchase-groups/save`, data, getApiHeader());
};



const getPlantPurchaseGroupByPlant = (plant) => {
  return axios.get(`${USER_API}plant-purchase-groups/plant/${plant}`, getApiHeader());
};

const getPlantPurchaseGroupByPurchaseOrg = (purchaseOrg) => {
  return axios.get(`${USER_API}plant-purchase-groups/purchaseOrg/${purchaseOrg}`, getApiHeader());
};

const getPoPricingConditions = (params = {}) => {
  const extraFilters = [];
  if (params.purchaseGroup) {
    extraFilters.push({
      field: "purchaseGroup",
      operator: "EQUAL",
      values: [String(params.purchaseGroup)],
    });
  }
  if (params.purchaseOrganisation) {
    extraFilters.push({
      field: "purchaseOrganisation",
      operator: "EQUAL",
      values: [String(params.purchaseOrganisation)],
    });
  }
  return listMasterCompat("poPricingConditions", { extraFilters });
};

const savePoPricingConditions = (data) => {
  return axios.post(`${USER_API}po-pricing-conditions/saveOrUpdate`, data, getApiHeader());
};

const pushPOToSAP = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.PO_CREATION, data);

const pushGRNToSAP = (data) => postSapProxy(SAP_PROXY_ENDPOINTS.GRN, data);

const sapGRNCancellation = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.GRN_CANCELLATION, data);

const sapServiceGRNCancellation = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.SERVICE_ENTRY_SHEET_CANCELLATION, data);




const pushServiceEntrySheetToSAP = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.SERVICE_ENTRY_SHEET, data);

// Invoice Park (MIRO/parking) to SAP
const parkInvoiceToSAP = (data) =>
  postSapProxy(SAP_PROXY_ENDPOINTS.PARK_INVOICE, data);

const getAllHsnCodes = () => {
  return axios.get(`${USER_API}hsn/getAllHsnCodes`, getApiHeader());
};



// Shipment APIs










const downloadAllVendorsData = () => {
  return axios.get(`${USER_API}vendor/reports`, getApiHeader());
};

const vendorReports = (params) => {
  const { page, size, vendorName, vendorCode } = params;
  let url = `${USER_API}/vendor/mdm-reports?page=${page}&size=${size}&sortBy=createdDate&sortDirection=asc`;
  if (vendorName) {
    url += `&vendorName=${encodeURIComponent(vendorName)}`;
  }
  if (vendorCode) {
    url += `&vendorCode=${encodeURIComponent(vendorCode)}`;
  }
  return axios.get(url, getApiHeader());
};

const getUnitOfMeasure = (options = {}) => listMasterCompat("uom", options);
const getMaterialGroup = (options = {}) =>
  listMasterCompat("materialGroup", options);
const getPaymentTerms = (options = {}) =>
  listMasterCompat("paymentTerms", options);
const getCurrency = (options = {}) => listMasterCompat("currency", options);

const getTaxCode = (options = {}) => listMasterCompat("taxCode", options);
const getPricingCondition = (options = {}) =>
  listMasterCompat("pricingCondition", options);


const buildMaterialSearchFilters = (filters = {}) => {
  const result = [];
  const pushFilter = (field, operator, value) => {
    const values = (Array.isArray(value) ? value : [value])
      .map((entry) => String(entry ?? "").trim())
      .filter(Boolean);
    if (values.length) {
      result.push({ field, operator, values });
    }
  };

  if (filters.plantnumber != null && filters.plantnumber !== "") {
    pushFilter("plantnumber", "EQUAL", filters.plantnumber);
  }
  if (filters.materialid != null && filters.materialid !== "") {
    pushFilter(
      "materialid",
      filters.exactMaterialid ? "EQUAL" : "LIKE",
      filters.materialid
    );
  }
  if (filters.description != null && filters.description !== "") {
    pushFilter("description", "LIKE", filters.description);
  }
  if (filters.uom != null && filters.uom !== "") {
    pushFilter("uom", "LIKE", filters.uom);
  }
  if (filters.materialGroup != null && filters.materialGroup !== "") {
    pushFilter("materialGroup", "EQUAL", filters.materialGroup);
  }
  if (filters.profitCenter != null && filters.profitCenter !== "") {
    pushFilter("profitCenter", "LIKE", filters.profitCenter);
  }
  if (filters.keyword != null && String(filters.keyword).trim() !== "") {
    const field =
      filters.keywordField === "description" ? "description" : "materialid";
    pushFilter(field, "LIKE", filters.keyword);
  }

  return result;
};

/** POST /masters/material/search */
const searchMaterials = async ({
  page = 0,
  size = 200,
  sorts = [],
  filters = {},
} = {}) => {
  const body = {
    page: { page, size },
    filters: buildMaterialSearchFilters(filters),
  };
  if (Array.isArray(sorts) && sorts.length) {
    body.sorts = sorts;
  }
  const response = await axios.post(
    `${USER_API}masters/material/search`,
    body,
    getApiHeader()
  );
  return response.data;
};

const unwrapSearchCount = (payload, rows = []) =>
  payload?.page?.totalElements ??
  payload?.count ??
  payload?.totalElements ??
  payload?.total ??
  rows.length;

/** POST /masters/material/search — keeps the previous listMaterialMasters response shape. */
const listMaterialMasters = async (
  materialId = null,
  pageIndex = 0,
  maxResults = 10,
  searchFilters = {}
) => {
  const payload = await searchMaterials({
    page: pageIndex,
    size: maxResults,
    filters: {
      ...searchFilters,
      ...(materialId
        ? { materialid: materialId, exactMaterialid: true }
        : {}),
    },
  });
  const rows = unwrapMasterRows(payload);
  return {
    data: {
      result: rows,
      count: unwrapSearchCount(payload, rows),
    },
  };
};

const getPurchasingOrganization = (options = {}) =>
  listMasterCompat("purchasingOrganization", options);

const getPurchasingOrganizationByCompanyCode = (companyCode) => {
  return axios.get(
    `${USER_API}purchasingOrganization/by-company-code/${companyCode}`,
    getApiHeader()
  );
};

const getPurchasingGroup = (options = {}) =>
  listMasterCompat("purchaseGroup", options);

const getCountryMaster = (options = {}) => listMasterCompat("country", options);

const getVendorTaxDetails = () => {
  return axios.get(`${USER_API}wt-master/getAll`, getApiHeader());
};
























/** POST /masters/company/search — company / country mapping rows. */
const normalizeCompanyCountryRow = (row = {}) => {
  const companyDescription =
    row.companyDescription ||
    row.companyCodeDesc ||
    row.description ||
    row.companyName ||
    "";
  return {
    ...row,
    countryKey: row.countryKey || row.country_key || row.countryCode || "",
    country: row.country || row.countryName || row.countryDesc || "",
    companyCode: row.companyCode || row.companycode || row.compCode || "",
    companyDescription,
    companyCodeDesc: row.companyCodeDesc || companyDescription,
    vendorIdentification: row.vendorIdentification || row.vendoridentification || "",
    taxRegistartionValidation:
      row.taxRegistartionValidation || row.taxRegistrationValidation || "",
    bankValidation: row.bankValidation || row.bankvalidation || "",
    description: row.description || companyDescription,
  };
};

const getCompanyCountries = async (options = {}) => {
  const result = await fetchMasterList("company", {
    page: 0,
    size: 1000,
    ...options,
  });
  return {
    data: (result.data || []).map(normalizeCompanyCountryRow),
    page: result.page,
  };
};

const getTaxCountryData = (countryCode) => {
  return axios.get(
    `${USER_API}wt-master/search?country=${countryCode}`,
    getApiHeader()
  );
};

const getTaxCodeData = (taxType) => {
  return axios.get(
    `${USER_API}wt-master/search?wtaxType=${taxType}`,
    getApiHeader()
  );
};

/** POST /masters/account-group/search */
const getAccountGroup = async ({
  page = 0,
  size = 1000,
  search = "",
  sorts = [],
} = {}) => {
  const result = await fetchMasterList("accountGroup", {
    page,
    size,
    search,
    searchField: "accountGroup",
    sorts,
  });
  return {
    data: result.data,
    page: result.page || unwrapMasterPage(result.raw, result.data),
  };
};

const getVendorCategories = (options = {}) =>
  listMasterCompat("vendorCategory", options);

const getcompanyCode = (options = {}) =>
  listMasterCompat("companyCode", options);

const getallRegion = (options = {}) => listMasterCompat("region", options);

const getRegionsForCountry = (country) => {
  return axios.get(`${USER_API}region/country/${country}`, getApiHeader());
};

const getTaxCategory = (options = {}) =>
  listMasterCompat("taxCategory", options);
const getVendorCategory = (options = {}) =>
  listMasterCompat("vendorCategory", options);

const approveVendor = (
  vendorId,
  isApproved,
  email,
  rejectedReason,
  isVendorApproved = false
) => {
  let extraParams = "";
  const currentDate = moment().format("DD-MM-YYYY HH:mm:ss");
  switch (parseInt(isApproved)) {
    case 1:
      extraParams = `&editedBy=${email}&editedDate=${currentDate}`;
      break;
    case 2:
      extraParams = `&approveBy=${email}&appprovedDate=${currentDate}`;
      break;
    case 3:
      extraParams = `&rejectedBy=${email}&reasonForRejection=${rejectedReason}&rejectedDate=${currentDate}`;
      break;
    default:
      extraParams = "";
      break;
  }
  if (isVendorApproved) {
    extraParams += `&editedBy=${email}&editedDate=${currentDate}`;
  }
  return axios.put(
    `${USER_API}vendor/update-approval?vendorId=${vendorId}&approved=${isApproved}${extraParams}`,
    getApiHeader()
  );
};

const validateVendor = (gstNumber, email, mobileNumber, bankAccNo, ifscCode = undefined) => {
  const params = new URLSearchParams();

  // Always append each param, defaulting to an empty string if undefined/null
  params.append("gstNumber", gstNumber ?? "");
  params.append("email", email ?? "");
  params.append("mobileNumber", mobileNumber ?? "");
  params.append("bankAccNo", bankAccNo ?? "");
  params.append("ifscCode", ifscCode ?? "");

  return axios.get(
    `${USER_API}vendor/validateVendor?${params.toString()}`,
    getApiHeader()
  );
};

const getAllLanguages = () => {
  return axios.get(`${USER_API}v1/getAllLanguage`, getApiHeader());
};
// console.log("getallRegion" , getallRegion);

export const resetPassword = async (userId, newPassword) => {
  try {
    const response = await axios.post(
      `${USER_API}/user/reset-password`,
      {
        userId,
        newPassword,
      },
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const pushRoleSearchFilter = (filters, field, operator, value) => {
  const values = (Array.isArray(value) ? value : [value])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean);
  if (!values.length) return;
  filters.push({
    field,
    operator: values.length > 1 && operator === "EQUAL" ? "IN" : operator,
    values,
  });
};

const buildRoleSearchFilters = (params = {}) => {
  const filters = [];
  const name = String(params.name ?? params.search ?? "").trim();
  if (name) {
    pushRoleSearchFilter(filters, "name", "LIKE", name);
  }
  const roleId = params.id ?? params.roleId;
  if (roleId != null && String(roleId).trim() !== "") {
    pushRoleSearchFilter(filters, "id", "EQUAL", roleId);
  }
  if (params.status != null && String(params.status).trim() !== "") {
    pushRoleSearchFilter(filters, "status", "EQUAL", params.status);
  }
  return filters;
};

/** POST /role/search — same { result, count } shape as the previous listRoles API. */
const getRoles = async (params = {}) => {
  const page = params.page ?? 0;
  const size = params.size ?? 10;
  const response = await axios.post(
    `${USER_API}role/search`,
    {
      page: { page, size },
      sorts: [],
      filters: buildRoleSearchFilters(params),
    },
    getApiHeader()
  );
  const payload = response.data;
  const rows = unwrapMasterRows(payload);
  return {
    ...payload,
    result: rows,
    count: unwrapSearchCount(payload, rows),
  };
};

const addRole = async (roleData) => {
  try {
    const response = await axios.post(
      `${USER_API}role/addRole`,
      roleData,
      getApiHeader()
    );
    return response;
  } catch (error) {
    throw error;
  }
};

const addRolePermissions = async (permissions) => {
  try {
    const response = await axios.post(
      `${USER_API}role/addRolePermissions`,
      permissions,
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    console.error("Error adding role permissions:", error);
    throw error;
  }
};

const updateRolePermissions = async (permissionId, permissions) => {
  try {
    const response = await axios.put(
      `${USER_API}role/updateRolePermissions/${permissionId}`,
      permissions,
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating role permissions:", error);
    throw error;
  }
};

const getDashboardData = () => {
  return axios.get(`${USER_API}vendor/getDashboardData`, getApiHeader());
};

const getVendorCountByGroups = () => {
  return axios.get(`${USER_API}vendor/count-by-groups`, getApiHeader());
};

const updateSapStatus = async (vendorId, pushToSapStatus) => {
  try {
    const response = await axios.put(
      `${USER_API}vendor/updateSapStatus?vendorId=${vendorId}&pushToSapStatus=${pushToSapStatus}`,
      getApiHeader()
    );
    return response;
  } catch (error) {
    throw error;
  }
};

const pushModuleSearchFilter = (filters, field, operator, value) => {
  const values = (Array.isArray(value) ? value : [value])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean);
  if (!values.length) return;
  filters.push({
    field,
    operator: values.length > 1 && operator === "EQUAL" ? "IN" : operator,
    values,
  });
};

const buildModuleSearchFilters = (params = {}) => {
  const filters = [];
  const name = String(params.name ?? params.moduleName ?? "").trim();
  const description = String(params.description ?? "").trim();
  if (name) {
    pushModuleSearchFilter(filters, "name", "LIKE", name);
  }
  if (description) {
    pushModuleSearchFilter(filters, "description", "LIKE", description);
  }
  if (params.status != null && String(params.status).trim() !== "") {
    pushModuleSearchFilter(filters, "status", "EQUAL", params.status);
  } else {
    pushModuleSearchFilter(filters, "status", "EQUAL", "1");
  }
  return filters;
};

/** POST /role/modules/search — same { result, count } shape as the previous listmodules API. */
const listModules = async (params = {}) => {
  const page = params.page ?? 0;
  const size = params.size ?? 10;
  const response = await axios.post(
    `${USER_API}role/modules/search`,
    {
      page: { page, size },
      sorts: [{ field: "lastmodifieddate", direction: "DESC" }],
      filters: buildModuleSearchFilters(params),
    },
    getApiHeader()
  );
  const payload = response.data;
  const rows = unwrapMasterRows(payload);
  return {
    ...payload,
    result: rows,
    count: unwrapSearchCount(payload, rows),
  };
};

/** POST /role/permissions/search — same { result, count } shape as listRolesPermissions. */
const listPermissions = async (params = {}) => {
  const page = params.page ?? 0;
  const size = params.size ?? 10;
  const filters = [];
  const roleId = params.roleId ?? params.role;
  if (roleId != null && String(roleId).trim() !== "") {
    pushRoleSearchFilter(filters, "role.id", "EQUAL", roleId);
  }
  const response = await axios.post(
    `${USER_API}role/permissions/search`,
    {
      page: { page, size },
      sorts: [],
      filters,
    },
    getApiHeader()
  );
  const payload = response.data;
  const rows = unwrapMasterRows(payload);
  return {
    ...payload,
    result: rows,
    count: unwrapSearchCount(payload, rows),
  };
};

const addModule = async (payload) => {
  try {
    const response = await axios.post(
      `${USER_API}role/addModule`,
      payload,
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const addUser = async (userData) => {
  return axios.post(`${USER_API}user/addUser`, userData, getApiHeader());
};

const getUserById = async (userId) => {
  const response = await axios.get(
    `${USER_API}user/getById/${userId}`,
    getApiHeader()
  );
  return response.data;
};

const updateUser = async (userId, userData) => {
  if (userId == null || userId === "") {
    return Promise.reject(new Error("updateUser: missing user id"));
  }
  return axios.put(`${USER_API}user/updateUser/${userId}`, userData, getApiHeader());
};

/** PUT /user/update-status/{id}?status=ACTIVE|DEACTIVE */
const updateUserStatus = async (userId, status) => {
  if (userId == null || userId === "") {
    return Promise.reject(new Error("updateUserStatus: missing user id"));
  }
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized !== "ACTIVE" && normalized !== "DEACTIVE") {
    return Promise.reject(new Error("updateUserStatus: status must be ACTIVE or DEACTIVE"));
  }
  const response = await axios.put(
    `${USER_API}user/update-status/${userId}?status=${normalized}`,
    {},
    getApiHeader()
  );
  return response.data;
};

const pushUserSearchFilter = (filters, field, operator, value) => {
  const values = (Array.isArray(value) ? value : [value])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean);
  if (!values.length) return;
  filters.push({
    field,
    operator: values.length > 1 && operator === "EQUAL" ? "IN" : operator,
    values,
  });
};

const buildUserSearchFilters = (params = {}) => {
  const filters = [];

  if (params.search) {
    pushUserSearchFilter(filters, "email", "LIKE", params.search);
  }
  if (params.roleId) {
    pushUserSearchFilter(filters, "roleId", "EQUAL", params.roleId);
  }

  const userIds = Array.isArray(params.userIds)
    ? params.userIds.map(Number).filter((id) => !Number.isNaN(id))
    : [];
  if (userIds.length) {
    pushUserSearchFilter(filters, "id", "EQUAL", userIds);
  } else if (params.userId != null && params.userId !== "") {
    pushUserSearchFilter(filters, "id", "EQUAL", params.userId);
  }

  if (params.email) {
    pushUserSearchFilter(filters, "email", "LIKE", params.email);
  }
  if (params.department) {
    pushUserSearchFilter(filters, "department", "EQUAL", params.department);
  }

  const companyCode = String(
    params.companyCode ?? params.companyCodeSelection ?? ""
  ).trim();
  if (companyCode) {
    pushUserSearchFilter(filters, "companyCodeSelection", "LIKE", companyCode);
  }

  const accountStatus = String(params.accountStatus || "").trim().toUpperCase();
  const includeAllStatuses =
    params.includeAllStatuses || params.includeInactive;
  if (accountStatus === "ACTIVE") {
    pushUserSearchFilter(filters, "status", "EQUAL", "1");
  } else if (accountStatus === "DEACTIVE") {
    pushUserSearchFilter(filters, "status", "EQUAL", "0");
  } else if (includeAllStatuses) {
    pushUserSearchFilter(filters, "status", "IN", ["0", "1"]);
  } else {
    pushUserSearchFilter(filters, "status", "EQUAL", "1");
  }

  return filters;
};

/** POST /user/search — same { result, count } shape as the previous listUsers API. */
const getUsers = async (params = {}) => {
  const userIds = Array.isArray(params.userIds)
    ? params.userIds.map(Number).filter((id) => !Number.isNaN(id))
    : [];
  const page = params.page ?? 0;
  const size =
    params.size ?? (userIds.length > 0 ? Math.max(userIds.length, 10) : 10);

  const response = await axios.post(
    `${USER_API}user/search`,
    {
      page: { page, size },
      sorts: [{ field: "lastmodifieddate", direction: "DESC" }],
      filters: buildUserSearchFilters(params),
    },
    getApiHeader()
  );
  const payload = response.data;
  const rows = unwrapMasterRows(payload);
  return {
    ...payload,
    result: rows,
    count: unwrapSearchCount(payload, rows),
  };
};

// Vendor-User Mapping APIs
const saveVendorUserMapping = async (payload) => {
  try {
    const response = await axios.post(
      `${USER_API}vendor-user-mapping/save`,
      payload,
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getVendorUserMappingByUserId = async (userId) => {
  try {
    const response = await axios.get(
      `${USER_API}vendor-user-mapping/get/${userId}`,
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const deleteVendorUserMapping = async (mappedId) => {
  try {
    const response = await axios.delete(
      `${USER_API}vendor-user-mapping/delete/${mappedId}`,
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getCostCenter = (options = {}) => listMasterCompat("costCenter", options);

const getCostCenterByCompanyCode = (companyCode) => {
  if (!companyCode) {
    return Promise.reject(new Error("Company code is required"));
  }
  return axios.get(`${USER_API}costCenter/getByCompanyCode/${companyCode}`, getApiHeader());
};

const getAllAsset = (options = {}) => listMasterCompat("asset", options);
const getAssetByCompanyCode = (companyCode) => {
  if (!companyCode) return Promise.reject(new Error("Company code is required"));
  return axios.get(
    `${USER_API}asset/by-company-code/${companyCode}`,
    getApiHeader()
  );
};

const getIncoterms = (options = {}) => listMasterCompat("incoterms", options);

const getGLAccounts = (options = {}) => listMasterCompat("glAccount", options);

const getFunctionalAreas = (options = {}) =>
  listMasterCompat("functionalArea", options);

const getGLAccountsByCompanyCode = (companyCode) => {
  if (!companyCode) {
    return Promise.reject(new Error("Company code is required"));
  }
  let dataUrl = `${USER_API}glAccount/${companyCode}`;
  return axios.get(dataUrl, getApiHeader());
};

const getPresignedUrl = (fileUrl) =>
  axios.post(
    `${USER_API}aws-s3/presigned-url`,
    { url: fileUrl },
    getApiHeader()
  );



// Approval Flow APIs






const getAllCertificateIds = (options = {}) =>
  listMasterCompat("certificateId", options);




const saveSubDepartment = (data) => {
  return axios.post(
    `${USER_API}/subdepartments/saveSubDepartment`,
    data,
    getApiHeader()
  );
};

const listSubDepartments = async (params = {}) => {
  const extraFilters = [];
  if (params.departmentId) {
    extraFilters.push({
      field: "departmentId",
      operator: "EQUAL",
      values: [String(params.departmentId)],
    });
  }
  const result = await fetchMasterList("subDepartments", {
    page: params.page ?? 0,
    size: params.size ?? 10,
    search: params.search || "",
    searchField: "subDepartmentName",
    extraFilters,
  });
  return {
    data: result.data,
    result: result.data,
    count: result.page.totalElements,
    totalCount: result.page.totalElements,
    page: result.page,
  };
};

const saveDepartment = (data) => {
  return axios.post(
    `${USER_API}departments/saveDepartments`,
    data,
    getApiHeader()
  );
};

const listDepartments = async (params = {}) => {
  const extraFilters = [];
  if (params.countryId) {
    extraFilters.push({
      field: "countryId",
      operator: "EQUAL",
      values: [String(params.countryId)],
    });
  }
  const result = await fetchMasterList("departments", {
    page: params.page ?? 0,
    size: params.size ?? 10,
    search: params.search || "",
    searchField: "departmentName",
    extraFilters,
  });
  return {
    data: result.data,
    result: result.data,
    count: result.page.totalElements,
    totalCount: result.page.totalElements,
    page: result.page,
  };
};

const listRoleCountry = async (params) => {
  try {
    let searchQuery = "";
    let filterConditions = [];
    
    // Text search
    if (params?.search) {
      searchQuery = `(r.roleName LIKE '%${params.search}%' OR c.description LIKE '%${params.search}%' OR d.departmentName LIKE '%${params.search}%' OR sd.subDepartmentName LIKE '%${params.search}%') AND`;
    }
    
    // Filter conditions
    if (params?.roleId) {
      filterConditions.push(`t.roleId = '${params.roleId}'`);
    }
    if (params?.countryId) {
      filterConditions.push(`t.countryId = '${params.countryId}'`);
    }
    if (params?.departmentId) {
      filterConditions.push(`t.departmentId = '${params.departmentId}'`);
    }
    if (params?.subDepartmentId) {
      filterConditions.push(`t.subDepartmentId = '${params.subDepartmentId}'`);
    }
    
    // Combine search and filters
    let finalQuery = searchQuery;
    if (filterConditions.length > 0) {
      finalQuery += filterConditions.join(' AND ') + ' AND';
    }
    
    const response = await axios.post(
      `${USER_API}roleCountry/listRoleCountry`,
      {
        searchQuery: `${finalQuery} t.status = 'ACTIVE' order by t.lastModifiedDate desc`,
        pageIndex: params.page,
        maxResults: params.size,
      },
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const saveRoleCountry = async (data) => {
  return axios.post(`${USER_API}roleCountry/saveRoleCountry`, data, getApiHeader());
};



const saveApprovalThreshold = async (data) => {
  return axios.post(`${USER_API}approval-threshold/saveApprovalThreshold`, data, getApiHeader());
};

const pushApprovalThresholdFilter = (filters, field, value) => {
  if (value == null || String(value).trim() === "") return;
  filters.push({
    field,
    operator: "EQUAL",
    values: [String(value).trim()],
  });
};

/** POST /approval-threshold/search — same { result, count } shape as the previous list API. */
const listApprovalThresholds = async (params = {}) => {
  const page = params.page ?? 0;
  const size = params.size ?? 10;
  const filters = [];
  pushApprovalThresholdFilter(filters, "roleId", params.roleId);
  pushApprovalThresholdFilter(filters, "departmentId", params.departmentId);
  pushApprovalThresholdFilter(filters, "status", params.status);

  const response = await axios.post(
    `${USER_API}approval-threshold/search`,
    {
      page: { page, size },
      sorts: [{ field: "lastmodifieddate", direction: "DESC" }],
      filters,
    },
    getApiHeader()
  );
  const payload = response.data;
  const rows = unwrapMasterRows(payload);
  return {
    ...payload,
    result: rows,
    count: unwrapSearchCount(payload, rows),
  };
};





const getAllPurchaseOrders = async (params) => {
  try {
    let searchQuery = "";
    if (params?.poNumber) {
      searchQuery += `t.poNumber LIKE '%${params.poNumber}%' AND `;
    }
    if (params?.id) {
      searchQuery += `t.id='${params.id}' AND `;
    }
    if (params?.poUniversalNumber) {
      searchQuery += `t.poUniversalNumber LIKE '%${params.poUniversalNumber}%' AND `;
    }
    if (params?.poStatus) {
      const raw = String(params.poStatus || "").trim();
      if (raw.includes(",")) {
        const statuses = raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => `'${s}'`);
        if (statuses.length > 0) {
          searchQuery += `t.poStatus IN (${statuses.join(",")}) AND `;
        }
      } else {
        searchQuery += `t.poStatus='${raw}' AND `;
      }
    }
    if (params?.vendorName) {
      searchQuery += `t.vendorName LIKE '%${params.vendorName}%' AND `;
    }
    if (params?.vendorId) {
      searchQuery += `t.vendorId='${params.vendorId}' AND `;
    }
    if (params?.poType) {
      searchQuery += `t.poType='${params.poType}' AND `;
    }
    if (params?.rfqNumber) {
      searchQuery += `t.rfqNumber LIKE '%${params.rfqNumber}%' AND `;
    }
    if (params?.quotationNo) {
      searchQuery += `t.quotationNo LIKE '%${params.quotationNo}%' AND `;
    }
    if (params?.totalPrice) {
      searchQuery += `t.totalPrice >= ${params.totalPrice} AND `;
    }
    if (params?.createdDate) {
      searchQuery += `DATE(t.createdDate) = '${params.createdDate}' AND `;
    }
    if (params?.companyCode) {
      searchQuery += `t.companyCode='${String(params.companyCode).replace(/'/g, "''")}' AND `;
    } else if (Array.isArray(params?.companyCodes) && params.companyCodes.length > 0) {
      const companyCodeList = params.companyCodes
        .map((c) => String(c || "").trim())
        .filter(Boolean)
        .map((c) => `'${c.replace(/'/g, "''")}'`);
      if (companyCodeList.length > 0) {
        searchQuery += `t.companyCode IN (${companyCodeList.join(",")}) AND `;
      }
    } else if (params?.forceEmptyCompanyCodeFilter) {
      // Internal user with no company codes mapped from their countries
      searchQuery += `1=0 AND `;
    }
    
    const response = await axios.post(
      `${USER_API}purchaseOrders/listPurchaseOrder`,
      {
        searchQuery: `${searchQuery} t.status = 1 order by t.lastmodifieddate desc`,
        pageIndex: params.page,
        maxResults: params.size,
      },
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const saveInvoice = async (payload) => {
  try {
    const response = await axios.post(
      `${USER_API}invoice/saveInvoice`,
      payload,
      getApiHeader()
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Email notification APIs
const sendApprovalEmail = async ({
  documentType,
  documentId,
  toEmails,
  status,
  notificationType,
}) => {
  return axios.post(
    `${USER_API}mail/send-approval`,
    {
      documentType,
      documentId,
      toEmails,
      ...(status != null && status !== "" ? { status } : {}),
      ...(notificationType ? { notificationType } : {}),
    },
    getApiHeader()
  );
};

// Dashboard status count APIs











const getInvoiceDetails = async (sapInvoiceNumber, invoiceYear) => {
  const params = {
    sapInvoiceNumber,
    invoiceYear,
  };
  const response = await axios.get(
    `${USER_API}invoice/details`,
    {
      params,
      ...getApiHeader()
    }
  );
  return response.data;
};



const getVendorBankKycStatusCount = () => {
  return axios.get(
    `${USER_API}vendor/bank-kyc/statusCount`,
    getApiHeader()
  );
};

const getVendorSAPPushStatus = () => {
  return axios.get(
    `${USER_API}vendor/sap-push-status`,
    getApiHeader()
  );
};


// Audit Log
const saveAuditLog = (payload) => {
  return axios.post(`${USER_API}auditLog/saveAuditLog`, payload, getApiHeader());
};

const getAuditLogByRecordId = (recordId) => {
  return axios.get(`${USER_API}auditLog/getByRecordId/${recordId}`, getApiHeader());
};

export const VendorService = {
  listVendor,
  searchVendors,
  saveVendor,
  createVendor,
  vendorChange,
  getPlant,
  getPlantsByCompanyCode,
  getAllStoragePlants,
  getPaymentTerms,
  getCurrency,
  getUnitOfMeasure,
  getMaterialGroup,
  getTaxCode,
  getPricingCondition,
  searchMaterials,
  listMaterialMasters,
  getPurchasingOrganization,
  getPurchasingOrganizationByCompanyCode,
  getPurchasingGroup,
  getCountryMaster,
  getTaxCountryData,
  getTaxCodeData,
  getCompanyCountries,
  saveDocumentsAndImages,
  getVendorTaxDetails,
  getcompanyCode,
  getAccountGroup,
  getallRegion,
  getRegionsForCountry,
  getTaxCategory,
  getVendorCategory,
  approveVendor,
  validateVendor,
  getAllLanguages,
  vendorReports,
  getVendorCategories,
  resetPassword,
  getRoles,
  addRole,
  addRolePermissions,
  updateRolePermissions,
  getDashboardData,
  getVendorCountByGroups,
  updateSapStatus,
  vendorBlockUnblock,
  downloadAllVendorsData,
  addModule,
  listModules,
  listPermissions,
  addUser,
  updateUser,
  updateUserStatus,
  getUsers,
  getUserById,
  saveVendorUserMapping,
  getVendorUserMappingByUserId,
  deleteVendorUserMapping,
  getCostCenter,
  getCostCenterByCompanyCode,
  getAllAsset,
  getAssetByCompanyCode,
  getGLAccounts,
  getGLAccountsByCompanyCode,
  getFunctionalAreas,
  getIncoterms,
  getPresignedUrl,
  getAllCertificateIds,
  saveSubDepartment,
  listSubDepartments,
  saveDepartment,
  listDepartments,
  listRoleCountry,
  saveRoleCountry,
  saveApprovalThreshold,
  listApprovalThresholds,
  getAllPurchaseOrders,
  getPlantPurchaseGroup,
  savePlantPurchaseGroup,
  getPlantPurchaseGroupByPlant,
  getPlantPurchaseGroupByPurchaseOrg,
  getPoPricingConditions,
  savePoPricingConditions,
  pushPOToSAP,
  getAllHsnCodes,
  pushGRNToSAP,
  pushServiceEntrySheetToSAP,
  parkInvoiceToSAP,
  saveInvoice,
  getVendorBankKycStatusCount,
  getVendorSAPPushStatus,
  sapGRNCancellation,
  sapServiceGRNCancellation,
  sendApprovalEmail,
  getInvoiceDetails,
  saveAuditLog,
  getAuditLogByRecordId,
};

export default VendorService;
